import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Trophy, Users, Dice5, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DrawReveal from "@/components/DrawReveal";

/**
 * Lottery draw dialog — fetches lottery stats on open and lets admin/merchant
 * trigger (or re-trigger) a random winners draw. Weighted by (1 + referrals).
 *
 * Props:
 *   open, onOpenChange
 *   game — { id, title, winners_count }
 */
export default function LotteryDrawDialog({ open, onOpenChange, game }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [winnersCount, setWinnersCount] = useState(game?.winners_count || 1);
  const [drawing, setDrawing] = useState(false);
  const [winners, setWinners] = useState([]);
  const [reveal, setReveal] = useState(null); // null | { phase, winners }

  useEffect(() => {
    if (!open || !game?.id) return;
    setWinners([]);
    setStats(null);
    api
      .get(`/games/${game.id}/lottery/stats`)
      .then((r) => {
        setStats(r.data);
        if (r.data.winners_count) setWinnersCount(r.data.winners_count);
        if (r.data.winners?.length) setWinners(r.data.winners);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [open, game?.id]);

  async function runDraw() {
    if (drawing) return;
    setDrawing(true);
    // Suspense: 5 s countdown overlay while the draw runs server-side.
    setReveal({ phase: "countdown", winners: [] });
    const suspense = new Promise((res) => setTimeout(res, 5000));
    try {
      const [{ data }] = await Promise.all([
        api.post(`/games/${game.id}/lottery/draw`, { winners_count: winnersCount }),
        suspense,
      ]);
      setWinners(data.winners || []);
      setReveal({ phase: "winners", winners: data.winners || [] });
      // Refresh stats
      const s = await api.get(`/games/${game.id}/lottery/stats`);
      setStats(s.data);
    } catch (e) {
      setReveal(null);
      toast.error(formatApiError(e));
    } finally {
      setDrawing(false);
    }
  }

  const hasPrev = !!stats?.lottery_drawn_at;
  const alreadyDrew = hasPrev && winners.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="lottery-draw-dialog"
        onInteractOutside={(e) => { if (reveal) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (reveal) e.preventDefault(); }}
      >
        <DrawReveal
          open={!!reveal}
          phase={reveal?.phase}
          participants={stats?.total_participants || 0}
          winners={reveal?.winners || []}
          showTickets
          onClose={() => setReveal(null)}
        />
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" style={{ color: "#d4af37" }} />
            {t("lottery.dialog_title")}
          </DialogTitle>
          <DialogDescription>{game?.title}</DialogDescription>
        </DialogHeader>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="qrise-card p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
              <Users className="h-3 w-3" />
              {t("lottery.total_participants")}
            </div>
            <div
              className="mt-1 text-3xl font-semibold tracking-tight"
              data-testid="lottery-total-participants"
            >
              {stats?.total_participants ?? "—"}
            </div>
          </div>
          <div className="qrise-card p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" />
              {t("lottery.configured_winners")}
            </div>
            <div className="mt-1 text-3xl font-semibold tracking-tight">
              {stats?.winners_count ?? game?.winners_count ?? 1}
            </div>
          </div>
        </div>

        {/* Winners count input + draw button */}
        <div className="rounded-xl border border-border p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[140px]">
            <Label className="text-xs font-medium">
              {t("lottery.winners_to_draw")}
            </Label>
            <Input
              type="number"
              min={1}
              max={stats?.total_participants || 100}
              value={winnersCount}
              onChange={(e) =>
                setWinnersCount(Math.max(1, parseInt(e.target.value) || 1))
              }
              className="mt-1.5 rounded-xl h-10"
              data-testid="lottery-winners-input"
            />
          </div>
          <Button
            type="button"
            onClick={runDraw}
            disabled={drawing || !stats?.total_participants}
            className="h-10 rounded-xl text-xs font-bold tracking-[0.2em] uppercase"
            style={{
              background:
                "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
              color: "#1a1410",
              boxShadow:
                "0 10px 30px -8px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.35)",
            }}
            data-testid="lottery-run-draw-btn"
          >
            {drawing ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                {t("lottery.drawing")}
              </>
            ) : alreadyDrew ? (
              <>
                <RefreshCw className="h-4 w-4 mr-1.5" />
                {t("lottery.redraw")}
              </>
            ) : (
              <>
                <Dice5 className="h-4 w-4 mr-1.5" />
                {t("lottery.draw_now")}
              </>
            )}
          </Button>
        </div>

        {/* Winners list */}
        {winners.length > 0 && (
          <div
            className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-4"
            data-testid="lottery-winners-list"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5 mb-3">
              <Trophy className="h-3.5 w-3.5" />
              {t("lottery.winners_title", { count: winners.length })}
            </div>
            <ul className="space-y-2">
              {winners.map((w, i) => (
                <li
                  key={w.session_id}
                  className="flex items-center gap-3 rounded-lg bg-card border border-border px-3 py-2"
                  data-testid={`lottery-winner-${i}`}
                >
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      background:
                        "linear-gradient(180deg,#f5e4bf,#d4af37,#a98027)",
                      color: "#1a1410",
                    }}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {w.first_name} {w.last_name}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono truncate">
                      {w.email} · {w.phone}
                    </div>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-300 bg-amber-400/15 px-2 py-1 rounded-full">
                    {w.tickets} {t("lottery.tickets")}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Previous-draw note */}
        {hasPrev && !drawing && (
          <div className="text-[11px] text-muted-foreground text-center">
            {t("lottery.drawn_at", {
              date: new Date(stats.lottery_drawn_at).toLocaleString(),
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
